import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowRight, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface BannerSettings {
  enabled: boolean;
  badge_text: string;
  headline: string;
  subheading: string;
  price_label: string;
  price: string;
  value_props: string[];
  cta_text: string;
  cta_link: string;
}

const HomepageBanner = () => {
  const [settings, setSettings] = useState<BannerSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'homepage_banner')
          .maybeSingle();

        if (!error && data?.setting_value) {
          setSettings(data.setting_value as unknown as BannerSettings);
        }
      } catch (error) {
        console.error('Error loading banner settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Don't render if loading, no settings, or disabled
  if (loading || !settings || !settings.enabled) {
    return null;
  }

  return (
    <section className="bg-primary border-t-4 border-accent py-10 md:py-14">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-10">
          {/* Left side - Icon and badge */}
          <div className="flex items-center gap-5">
            <div className="hidden sm:flex items-center justify-center w-20 h-20 rounded-2xl bg-accent">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <div className="text-center sm:text-left">
              {settings.badge_text && (
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold bg-accent text-white rounded-full mb-3 uppercase tracking-wider">
                  <Sparkles className="w-3 h-3" />
                  {settings.badge_text}
                </span>
              )}
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                {settings.headline}
              </h3>
              <p className="text-white/90 text-base md:text-lg mt-1 font-medium">
                {settings.subheading}
              </p>
            </div>
          </div>

          {/* Center - Value props */}
          <div className="flex flex-wrap justify-center gap-3 lg:gap-4">
            {settings.value_props.map((prop) => (
              <div
                key={prop}
                className="flex items-center gap-2 px-4 py-2 bg-transparent border-2 border-white/50 rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-accent" />
                <span className="text-white text-sm md:text-base font-semibold whitespace-nowrap">
                  {prop}
                </span>
              </div>
            ))}
          </div>

          {/* Right side - Price and CTA */}
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="text-center sm:text-right">
              <p className="text-white/80 text-sm font-medium">{settings.price_label}</p>
              <p className="text-3xl md:text-4xl font-black text-white">{settings.price}</p>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-accent text-white hover:bg-accent/90 font-bold group text-base md:text-lg px-8 py-6 rounded-xl hover:scale-105 transition-all duration-200"
            >
              <Link to={settings.cta_link}>
                {settings.cta_text}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomepageBanner;
